import '@/i18n'; // Initialize i18n — must be before any component using useTranslation
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/lib/auth';
import { ErrorBoundary } from '@/components/features/error';
import { AuthGuard } from '@/components/features/auth';
import { AppShell } from '@/components/layout';
import { TooltipProvider } from '@/components/ui';
import { Toaster } from '@/components/ui/sonner';
import {
  DashboardPage,
  LoginPage,
  ForgotPasswordPage,
  ChangePasswordPage,
  NotFoundPage,
  WebhooksPage,
  WebhookDetailPage,
  TransactionsPage,
  TransactionTriggerPage,
  TransactionDetailPage,
  SubscriptionsPage,
  ServicesPage,
  ServiceDetailPage,
  IQServicesPage,
  IQServiceCreatePage,
  IQServiceDetailPage,
  WebhookConfigPage,
  MerchantsPage,
  MerchantDetailPage,
  MerchantCreatePage,
  UsersPage,
  UserCreatePage,
  UserDetailPage,
} from '@/pages';

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Protected route wrapper that includes AppShell and AuthGuard.
 */
function ProtectedRoute({
  children,
  requiredRoles,
}: {
  children: React.ReactNode;
  requiredRoles?: ('platform_admin' | 'merchant_admin' | 'merchant_viewer')[];
}) {
  return (
    <AuthGuard requiredRoles={requiredRoles}>
      <AppShell>{children}</AppShell>
    </AuthGuard>
  );
}

/**
 * Main application component.
 * Spec 0043: Callback-Route entfernt, Login/Forgot/Change-Password hinzugefuegt.
 */
export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Routes>
                {/* Public routes — kein AuthGuard */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/change-password" element={<ChangePasswordPage />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/merchants"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin', 'merchant_viewer']}>
                      <MerchantsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/merchants/new"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin']}>
                      <MerchantCreatePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/merchants/:id"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin', 'merchant_viewer']}>
                      <MerchantDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/webhooks"
                  element={
                    <ProtectedRoute>
                      <WebhooksPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/webhooks/config"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin']}>
                      <WebhookConfigPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/webhooks/:id"
                  element={
                    <ProtectedRoute>
                      <WebhookDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/transactions"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin']}>
                      <TransactionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/transactions/trigger"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin']}>
                      <TransactionTriggerPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/transactions/:transactionId"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin']}>
                      <TransactionDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/subscriptions"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin']}>
                      <SubscriptionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/services"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin']}>
                      <ServicesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/services/:id"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin', 'merchant_admin']}>
                      <ServiceDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/iq-services"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin']}>
                      <IQServicesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/iq-services/new"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin']}>
                      <IQServiceCreatePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/iq-services/:id"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin']}>
                      <IQServiceDetailPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin']}>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users/new"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin']}>
                      <UserCreatePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users/:sub"
                  element={
                    <ProtectedRoute requiredRoles={['platform_admin']}>
                      <UserDetailPage />
                    </ProtectedRoute>
                  }
                />

                {/* 404 catch-all */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </TooltipProvider>
            <Toaster />
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
